import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  type Campaign,
  CampaignClass,
  type Goal as GoalModel,
  type MailrailDripEmailCampaign,
} from '@feathr/blackbox';
import {
  Button,
  ButtonValid,
  CardContent,
  CardHeader,
  CardV2 as Card,
  Form,
  Toggle,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { flattenErrors } from '@feathr/hooks';

import AddGoals from './AddGoals';
import { getGoalSegments, validateStepGoals } from './StepGoals.utils';

import * as styles from '../AdWizard/AdWizard.css';

interface IButtonProps {
  goals: IObservableArray<GoalModel>;
  onNext?: () => void;
}

interface IProps extends IButtonProps {
  onPrev?: () => void;
  campaign: Campaign;
  disabled?: boolean;
}

const NextStepButton = observer(({ goals, onNext }: IButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const segments = getGoalSegments(goals, Segments);
  const validationErrors = validateStepGoals({ goals, goalSegments: segments, t });
  return (
    <ButtonValid errors={flattenErrors(validationErrors)} name={'next_step'} onClick={onNext}>
      {t('Next')}
    </ButtonValid>
  );
});

function StepGoals({
  campaign,
  disabled = false,
  goals,
  onNext,
  onPrev,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const hasVerticalWizard = [
    CampaignClass.Referral,
    CampaignClass.TrackedLink,
    CampaignClass.SmartPinpointEmail,
  ].includes(campaign.get('_cls'));

  const actions = hasVerticalWizard
    ? [
        <Button key={'prev'} name={'previous_step'} onClick={onPrev}>
          {t('Previous')}
        </Button>,
        <NextStepButton goals={goals} key={'next'} onNext={onNext} />,
      ]
    : [];

  function handleChangeExitOnConversion(value?: boolean): void {
    (campaign as MailrailDripEmailCampaign).set({ exit_on_conversion: !!value });
  }

  const goalsDescription = (
    <Trans t={t}>
      <p>
        Select a goal for this campaign. In Goal mode, track conversions when individuals who engage
        with this campaign join a group. In Advanced mode, track conversions using a Conversion
        Pixel to monitor people interacting with the campaign.
      </p>
      <p>
        <a
          href={'https://help.feathr.co/hc/en-us/articles/360047216094-What-is-a-Goal'}
          target={'_blank'}
        >
          Learn more about goal tracking
        </a>
      </p>
    </Trans>
  );

  return (
    <Form
      actions={actions}
      className={hasVerticalWizard ? undefined : styles.formRoot}
      label={t('Edit Campaign: Goals')}
      width={'wide'}
    >
      <Card name={'goals-card'} width={'wide'}>
        <CardHeader description={goalsDescription} title={t('Set Goals')} />
        <CardContent addVerticalGap={true}>
          <AddGoals goalKind={'campaign'} goals={goals} parent={campaign} />
          {campaign.isMailrailDrip && (
            <Toggle
              disabled={disabled}
              helpText={t(
                "Contacts who convert (or have already converted) on one of this campaign's goals will not receive its emails from that point forward.",
              )}
              label={t('Exclude contacts on conversion')}
              layout={'well'}
              onChange={handleChangeExitOnConversion}
              value={(campaign as MailrailDripEmailCampaign).get('exit_on_conversion', false)}
            />
          )}
        </CardContent>
      </Card>
    </Form>
  );
}

export default observer(StepGoals);
